import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { redirectToCheckout } from "../services/stripe";
import { getFounderProgramStatus } from "../utils/founderProgramService";
import "./LandingPage.css";

const MONTHLY_PRICE_ID = process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID || "";

const PLATFORM_MODULES = [
  {
    label: "Calculate",
    title: "Machining, fabrication, welding, quality, and estimating math",
    text:
      "Run practical shop calculators with visible formulas, unit support, and notes that explain when the result is an estimate.",
  },
  {
    label: "Plan",
    title: "Save work to jobs, customers, and manufacturing plans",
    text:
      "Keep calculations connected to the part, operation, machine, customer, and setup they were created for.",
  },
  {
    label: "Standardize",
    title: "Build SOPs, process routes, 3S work, and team knowledge",
    text:
      "Turn shop decisions into repeatable procedures, employee assignments, saved tools, material records, and resource references.",
  },
];

const WORKFLOW_STEPS = [
  "Print / Model",
  "Material",
  "Calculator",
  "Manufacturing Plan",
  "Machine Route",
  "SOP",
  "Production",
  "3S Review",
];

const FEATURE_CARDS = [
  {
    title: "GD&T Helper",
    text:
      "A flagship inspection helper for symbols, datum thinking, true position context, MMC/LMC meaning, and tolerance-zone interpretation.",
  },
  {
    title: "Manufacturing Calculators",
    text:
      "Machining, sheet metal, welding, cutting, additive, casting, material estimating, and quoting tools organized by manufacturing field.",
  },
  {
    title: "Manufacturing Plans",
    text:
      "Save calculator outputs under the job or customer, then build a connected record of setup notes, tooling, machines, and decisions.",
  },
  {
    title: "SOP Builder",
    text:
      "Create process steps with machines, tools, estimated time, costs, images, and ordered instructions for repeatable shop work.",
  },
  {
    title: "Tool Database",
    text:
      "Store cutters, drills, taps, end mills, lathe tooling, coolant, holders, inserts, and shop-specific tool records.",
  },
  {
    title: "3S Workspace",
    text:
      "Apply sort, set in order, and shine principles through practical projects, assignments, stages, photos, and improvement tracking.",
  },
];

const ROLE_CARDS = [
  {
    role: "Machinist",
    focus: "Fast calculations, saved tools, print interpretation, and setup decisions without hunting through spreadsheets.",
  },
  {
    role: "Shop Owner",
    focus: "Business accounts, employee invites, job visibility, quoting context, and work tied to customers.",
  },
  {
    role: "Programmer",
    focus: "Feeds, speeds, G-code context, machine-specific setup data, and operation notes that stay with the plan.",
  },
  {
    role: "Inspector",
    focus: "GD&T reference, true position, tolerance context, hole data, and quality calculations in one place.",
  },
];

const LandingPage = ({
  user,
  trialStatus = null,
  subscriptionStatus = null,
  hasAccess = false,
}) => {
  const history = useHistory();
  const [positionTolerance, setPositionTolerance] = useState("0.010");
  const [actualFeatureSize, setActualFeatureSize] = useState("0.257");
  const [mmcSize, setMmcSize] = useState("0.250");
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState("");
  const [founderStatus, setFounderStatus] = useState(null);

  useEffect(() => {
    let mounted = true;
    getFounderProgramStatus()
      .then((status) => {
        if (mounted) setFounderStatus(status);
      })
      .catch(() => {
        if (mounted) setFounderStatus(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const gdtPreview = useMemo(() => {
    const tolerance = parseFloat(positionTolerance);
    const actual = parseFloat(actualFeatureSize);
    const mmc = parseFloat(mmcSize);

    if (!Number.isFinite(tolerance) || tolerance <= 0) {
      return {
        bonus: "More data required",
        total: "More data required",
      };
    }

    const bonus =
      Number.isFinite(actual) && Number.isFinite(mmc)
        ? Math.max(0, actual - mmc)
        : 0;

    return {
      bonus: bonus.toFixed(4),
      total: (tolerance + bonus).toFixed(4),
    };
  }, [actualFeatureSize, mmcSize, positionTolerance]);

  const isExpiredUser =
    !!user &&
    !hasAccess &&
    subscriptionStatus !== "active" &&
    trialStatus?.isTrialExpired;

  const handleUpgrade = async () => {
    if (!user?.uid || !user?.email) {
      history.push("/auth");
      return;
    }

    setBillingError("");
    setBillingLoading(true);

    try {
      await redirectToCheckout({
        email: user.email,
        uid: user.uid,
        priceId: MONTHLY_PRICE_ID,
        accountType: "personal",
      });
    } catch (error) {
      console.error("Billing redirect error:", error);
      setBillingError(
        error?.message || "Failed to start billing. Please try again."
      );
    } finally {
      setBillingLoading(false);
    }
  };

  const primaryCta = user && hasAccess ? (
    <Link to="/dashboard" className="landing-btn landing-btn-primary">
      Open Dashboard
    </Link>
  ) : (
    <Link to="/auth?mode=signup" className="landing-btn landing-btn-primary">
      Start 1-Hour Trial
    </Link>
  );

  return (
    <div className="landing-page">
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-copy">
            <span className="landing-badge">
              {isExpiredUser
                ? "Trial ended"
                : founderStatus?.remaining > 0
                  ? `First 100 users get lifetime access - ${founderStatus.remaining} spots left`
                  : "Manufacturing Operating System"}
            </span>

            <h1>
              {isExpiredUser
                ? "Restore access to your manufacturing workspace."
                : "The Manufacturing Operating System"}
            </h1>

            <p className="landing-subtext">
              {isExpiredUser
                ? "Continue to billing to restore ManuCalc access, including calculators, GD&T, manufacturing plans, SOPs, saved tools, and shop workflow tools."
                : "ManuCalc brings the AI Manufacturing Copilot, calculators, manufacturing plans, SOP Builder, setup sheets, inspection, tool libraries, collaboration, messaging, and shop management into one clean workspace."}
            </p>

            <div className="landing-cta-row">
              {isExpiredUser ? (
                <>
                  <button
                    className="landing-btn landing-btn-primary"
                    onClick={handleUpgrade}
                    disabled={billingLoading}
                  >
                    {billingLoading ? "Loading..." : "Restore Access"}
                  </button>
                  <Link to="/auth" className="landing-btn landing-btn-secondary">
                    Switch Account
                  </Link>
                </>
              ) : (
                <>
                  {primaryCta}
                  <Link
                    to="/auth?mode=signup&accountType=company"
                    className="landing-btn landing-btn-secondary"
                  >
                    Create Business Account
                  </Link>
                  <a href="#features" className="landing-btn landing-btn-quiet">
                    Explore Features
                  </a>
                  <a href="#product-preview" className="landing-btn landing-btn-quiet">
                    Watch Demo
                  </a>
                </>
              )}
            </div>

            {billingError && <p className="landing-error">{billingError}</p>}

            <div className="landing-trust">
              <span>First 100 users receive lifetime free access</span>
              <span>Individual and business accounts</span>
              <span>AI, calculators, planning, messaging, and 3S</span>
            </div>
          </div>

          <div id="product-preview" className="landing-preview" aria-label="ManuCalc product preview">
            <div className="preview-shell">
              <div className="preview-topbar">
                <span className="preview-dot" />
                <span className="preview-dot" />
                <span className="preview-dot" />
                <strong>ManuCalc Control Center</strong>
              </div>

              <div className="preview-content">
                <div className="preview-header">
                  <p className="preview-kicker">Flagship workflow</p>
                  <h2>GD&amp;T Helper</h2>
                  <p>
                    Interpret symbol meaning, calculate inspection context, and
                    keep the result tied to the job that needs it.
                  </p>
                </div>

                <div className="preview-result">
                  <span>Total position tolerance at MMC</span>
                  <strong>{gdtPreview.total}</strong>
                </div>

                <div className="preview-form">
                  <div className="preview-field">
                    <label>Position tolerance (in)</label>
                    <input
                      inputMode="decimal"
                      value={positionTolerance}
                      onChange={(event) => setPositionTolerance(event.target.value)}
                    />
                  </div>
                  <div className="preview-field">
                    <label>Actual hole size (in)</label>
                    <input
                      inputMode="decimal"
                      value={actualFeatureSize}
                      onChange={(event) => setActualFeatureSize(event.target.value)}
                    />
                  </div>
                  <div className="preview-field">
                    <label>MMC hole size (in)</label>
                    <input
                      inputMode="decimal"
                      value={mmcSize}
                      onChange={(event) => setMmcSize(event.target.value)}
                    />
                  </div>
                  <div className="preview-field">
                    <label>Bonus tolerance</label>
                    <input value={gdtPreview.bonus} readOnly />
                  </div>
                </div>

                <div className="preview-workflow">
                  {WORKFLOW_STEPS.map((step) => (
                    <span key={step}>{step}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!isExpiredUser && (
        <>
          <section className="landing-section landing-section-tight">
            <div className="landing-section-heading">
              <span>One platform</span>
              <h2>From print math to production workflow.</h2>
              <p>
                ManuCalc is designed to move with the job: calculate the value,
                save the result, route the work, build the SOP, and keep the
                knowledge available for the next person who needs it.
              </p>
            </div>

            <div className="landing-module-grid">
              {PLATFORM_MODULES.map((module) => (
                <article key={module.title} className="landing-module-card">
                  <span>{module.label}</span>
                  <h3>{module.title}</h3>
                  <p>{module.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="landing-section" id="features">
            <div className="landing-section-heading">
              <span>Core tools</span>
              <h2>A broader manufacturing calculator platform.</h2>
              <p>
                The calculator library is only the start. ManuCalc also includes
                planning, reference, team, and improvement tools so shop math
                does not live separately from shop execution.
              </p>
            </div>

            <div className="landing-feature-grid">
              {FEATURE_CARDS.map((card) => (
                <article key={card.title} className="landing-feature-card">
                  <div className="landing-feature-icon">{card.title.slice(0, 2)}</div>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="landing-section landing-split">
            <div className="landing-section-heading align-left">
              <span>Who it serves</span>
              <h2>Useful for a solo user. Structured enough for a shop.</h2>
              <p>
                Individual users can get straight to calculations and saved work.
                Business accounts add employee invites, shared manufacturing
                plans, role-aware access, and team visibility.
              </p>
              <div className="landing-cta-row">
                <Link to="/auth?mode=signup" className="landing-btn landing-btn-primary">
                  Start Trial
                </Link>
                <Link to="/about" className="landing-btn landing-btn-secondary">
                  About ManuCalc
                </Link>
              </div>
            </div>

            <div className="landing-role-grid">
              {ROLE_CARDS.map((card) => (
                <article key={card.role} className="landing-role-card">
                  <strong>{card.role}</strong>
                  <p>{card.focus}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="landing-pricing">
            <div className="pricing-card">
              <span className="pricing-badge">Flexible account paths</span>
              <h2>Start with full access. Decide after the trial.</h2>
              <p>
                Every new individual or business owner account starts with a
                1-hour unrestricted in-app trial before billing checkout is
                required. Employee invite signups are routed into the company
                workspace and should not be sent to billing.
              </p>

              <ul>
                <li>Full calculator and GD&T access during the trial</li>
                <li>Manufacturing Plans, SOP Builder, 3S, tool data, and resources</li>
                <li>Business accounts for employee invites, seats, and dashboard controls</li>
                <li>Custom calculator requests routed to the ManuCalc platform owner</li>
              </ul>

              {!hasAccess && (
                <button
                  className="landing-btn landing-btn-primary"
                  onClick={handleUpgrade}
                  disabled={billingLoading}
                >
                  {billingLoading ? "Loading..." : "Continue to Billing"}
                </button>
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default LandingPage;
